"use client";

import type { CSSProperties, JSX } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@repo/ui/carousel";
import { CustomCursorArea } from "@repo/ui/custom-cursor";
import { useTranslations } from "next-intl";
import { cn } from "@repo/ui/utils";

import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import { VisualWrapper } from "@/components/common/visual/visual.component";

import type { VisualType } from "@/lib/types";

import type {
  FeatureSliderItem,
  FeatureSliderProps,
} from "./feature-slider.types";

const FADE: CSSProperties = {
  transitionDuration: "var(--motion-duration, 700ms)",
};

function SliderCounter({ total }: { total: number }): JSX.Element {
  const { selectedIndex } = useCarousel();
  return (
    <p
      className="typography-component-sm-medium flex items-center justify-center gap-4 text-white/80"
      aria-live="polite"
      data-testid="feature-slider-counter"
    >
      <span className="text-white">{selectedIndex + 1}</span>
      <span aria-hidden="true" className="block h-px w-16 bg-white/40" />
      <span>{total}</span>
    </p>
  );
}

function SliderSlide({
  item,
  index,
}: {
  item: FeatureSliderItem;
  index: number;
}): JSX.Element {
  const { selectedIndex } = useCarousel();
  const isActive = index === selectedIndex;

  return (
    <div
      className={cn(
        "flex select-none flex-col items-center gap-6 px-4 text-center transition-opacity ease-out tablet:px-0",
        isActive ? "opacity-100" : "opacity-25",
      )}
      style={FADE}
      data-active={isActive || undefined}
    >
      <h2
        className={cn(
          "typography-mobile-display-fit desktop:typography-desktop-display-d1",
          "whitespace-nowrap text-white",
        )}
        data-testid={isActive ? "feature-slider-title" : undefined}
      >
        {item.title}
      </h2>
      {item.body ? (
        <RichTextCondensed
          richText={item.body}
          as="body-lg"
          className="max-w-[520px] text-white/90"
        />
      ) : null}
    </div>
  );
}

function SliderBackgrounds({
  items,
  fallback,
}: {
  items: FeatureSliderItem[];
  fallback?: VisualType;
}): JSX.Element {
  const { selectedIndex } = useCarousel();
  const layers = items.map((item) => ({
    key: item._key,
    visual: item.background ?? fallback,
  }));

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 -z-10 overflow-hidden"
      data-testid="feature-slider-backgrounds"
    >
      {layers.map((layer, index) =>
        layer.visual ? (
          <VisualWrapper
            key={layer.key}
            visual={layer.visual}
            className={cn(
              "absolute inset-0 h-full w-full transition-opacity ease-out",
              index === selectedIndex ? "opacity-100" : "opacity-0",
            )}
            style={FADE}
            visualClassName="h-full w-full"
            imgClassName="visual-zoom h-full w-full object-cover"
            asBackground
          />
        ) : null,
      )}
      <div className="absolute inset-0 bg-black/35" />
    </div>
  );
}

function SliderImages({
  items,
}: {
  items: FeatureSliderItem[];
}): JSX.Element | null {
  const { selectedIndex } = useCarousel();
  if (!items.some((item) => item.visual)) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex translate-y-[35%] justify-center"
      data-testid="feature-slider-image"
    >
      <div className="visual-float relative aspect-[243/214] w-[150px] [--visual-float-direction:-1] [--visual-float-distance:28px] desktop:w-[243px]">
        {items.map((item, index) =>
          item.visual ? (
            <VisualWrapper
              key={item._key}
              visual={item.visual}
              className={cn(
                "absolute inset-0 h-full w-full transition-opacity ease-out",
                index === selectedIndex ? "opacity-100" : "opacity-0",
              )}
              style={FADE}
              visualClassName="h-full w-full"
              imgClassName="h-full w-full object-cover"
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

export function FeatureSlider({
  background,
  items,
  className,
}: FeatureSliderProps): JSX.Element | null {
  const t = useTranslations("navigation");

  if (items.length === 0) return null;

  return (
    <section
      className={cn(
        "scroll-motion-scope dark relative isolate z-10 flex w-full flex-col text-white",
        "min-h-[640px] desktop:min-h-[900px]",
        className,
      )}
      data-testid="feature-slider"
    >
      <Carousel
        align="center"
        loop
        wheelGestures
        aria-label={t("featureSlides")}
        className="flex flex-1 flex-col gap-12 pb-32 pt-16 desktop:pb-40 desktop:pt-20"
      >
        <SliderBackgrounds items={items} fallback={background} />
        <SliderCounter total={items.length} />

        <CustomCursorArea type="drag" className="flex flex-1 items-center">
          <CarouselContent
            className="ml-0 items-center"
            viewportClassName="w-full"
          >
            {items.map((item, index) => (
              <CarouselItem
                key={item._key}
                className="basis-[85%] pl-0 tablet:basis-[70%] desktop:basis-[55%]"
              >
                <SliderSlide item={item} index={index} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </CustomCursorArea>

        <SliderImages items={items} />
      </Carousel>
    </section>
  );
}
